/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useRef, CSSProperties } from "react";
import { useEffect } from "react";
import clsx from "clsx";
import { ITabs } from "../../types/customizer";
import './index.scss';
import leftArrow from "../../../assets/images/left-chevrolet.svg";
import lashbrookLogo from '../../../assets/images/LB_Logoblue.png'
import { useRingCustomizerContext } from "../../context/RingCustomizerContext";
import { FONT_MAP, getSelectedInfo } from "../../../utils/commonRingUtils";
import If from "../If";
import { APP_STYLES, DEFAULT_STYLES } from "../../../utils/basic";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { formatSummaryDate } from "../Summary";
import LegacyPinWithHelpModal from "../Pin";

interface Props {
	pages: ITabs;
	selectedTab: string;
	handleActiveTab: Function;
	price: string;
	shippingOptions: any;
	favouritesCount?: number;
	showFavourites?: boolean;
	onFavouritesClick?: () => void;
}

const DesktopSidebar = ({
	pages,
	selectedTab,
	handleActiveTab,
	price,
	shippingOptions,
	favouritesCount = 0,
	showFavourites = false,
	onFavouritesClick,
}: Props) => {
	const sidebarRef = useRef<HTMLDivElement>(null);
	const [isScrollable, setIsScrollable] = useState(false);
	const [horizontalStyle, setHorizontalStyle] = useState<CSSProperties>({});
	const [verticalStyle, setVerticalStyle] = useState<CSSProperties>({});
	const [acknowledged, setAcknowledged] = useState(false);
	const [, setIsPinValid] = useState(false);
	const { setHandleActiveTab } = useRingCustomizerContext();

	useEffect(() => {
		setHandleActiveTab(handleActiveTab as any);
		return () => setHandleActiveTab(null);
	}, [handleActiveTab, setHandleActiveTab]);

	const checkScrollable = useCallback(() => {
		const el = sidebarRef.current;
		if (el) {
			setIsScrollable(el.scrollHeight > el.clientHeight);
		}
	}, []);

	useEffect(() => {
		const el = sidebarRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => {
			requestAnimationFrame(checkScrollable);
		});
		ro.observe(el);
		checkScrollable();
		return () => ro.disconnect();
	}, [checkScrollable]);

	useEffect(() => {
		requestAnimationFrame(checkScrollable);
	}, [pages, checkScrollable]);

	useEffect(() => {
		requestAnimationFrame(checkScrollable);
	}, [showFavourites, checkScrollable]);

	const activeId = showFavourites ? 'favourites' : selectedTab;

	useEffect(() => {
		// Use requestAnimationFrame to ensure DOM has updated before measuring
		requestAnimationFrame(() => {
			setHorizontalStyle(getHorizontalStyle());
			setVerticalStyle(getVerticalStyle());
		});
	}, [selectedTab, pages, showFavourites])
	const {
		subpath,
		product,
		handleLegacyAddToCart,
		handleAddToCart,
		handleDone,
	} = useRingCustomizerContext();
	const isShanecoPOS = subpath === 'shaneco-pos';

	const [activeModal, setActiveModal] = useState<null | 'pinHelp' | 'reviews'>(null);
	const reduxSaleDiscount = useAppSelector((state: RootState) => state.customizer.saleDiscount);
	const styles = APP_STYLES[subpath] || DEFAULT_STYLES;
	const saleDiscount = reduxSaleDiscount ?? 0;

	useEffect(() => {
		if (!activeModal) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setActiveModal(null);
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [activeModal]);


	const getHorizontalStyle = (): CSSProperties => {
		const tabContentRect = document.getElementById('main-content')?.getBoundingClientRect();
		const sidebarRect = document.getElementById(activeId)?.getBoundingClientRect();
		if (tabContentRect && sidebarRect) {
			let tabcontentTop = tabContentRect.top;
			const sidebarTopX = sidebarRect.top;
			const sidebarBottomX = sidebarRect.top + sidebarRect.height;

			const selectedPage = pages.find(item => item.eventKey === activeId);
			const hasItems = selectedPage?.items?.length || 0;
			tabcontentTop = tabcontentTop + (hasItems ? 47 : 2);

			if (tabcontentTop - sidebarTopX > 20 && tabcontentTop - sidebarBottomX > 20) { // If side tab above 20px of margin line
				return {
					bottom: '0px',
					top: 'unset',
				}
			} else if (sidebarTopX - tabcontentTop > 20 && sidebarBottomX - tabcontentTop > 20) { // If side tab below 20px of margin line
				return {
					top: '0px',
					bottom: 'unset'
				}
			} else if (tabcontentTop - sidebarTopX > 20) { // If sidebar top above 20px of margin line
				return {
					top: '0px',
					bottom: 'unset'
				}
			} else {
				return {
					bottom: '0px',
					top: 'unset',
				}
			}
		}
		return {}
	}

	const getVerticalStyle = (): CSSProperties => {
		const tabContentRect = document.getElementById('main-content')?.getBoundingClientRect();
		const sidebarRect = document.getElementById(activeId)?.getBoundingClientRect();
		if (tabContentRect && sidebarRect) {
			let tabcontentTop = tabContentRect.top;
			const sidebarTopX = sidebarRect.top;
			const sidebarBottomX = sidebarRect.top + sidebarRect.height;

			const selectedPage = pages.find(item => item.eventKey === activeId);
			const hasItems = selectedPage?.items?.length || 0;
			tabcontentTop = tabcontentTop + (hasItems ? 47 : 2);

			let height = 0;
			const buffer = styles.showfullBorder ? 2 : 0;
			if (tabcontentTop - sidebarTopX > 20 && tabcontentTop - sidebarBottomX > 20) { // If side tab above 20px of margin line
				height = tabcontentTop - sidebarBottomX;
				return {
					height: height + buffer + 'px',
					top: '100%',
					bottom: 'unset'
				}
			} else if (sidebarTopX - tabcontentTop > 20 && sidebarBottomX - tabcontentTop > 20) { // If side tab below 20px of margin line
				height = sidebarTopX - tabcontentTop;
				return {
					height: height + buffer + 'px',
					bottom: '100%',
					top: 'unset'
				}
			} else if (tabcontentTop - sidebarTopX > 20) { // If sidebar top above 20px of margin line
				height = tabcontentTop - sidebarTopX;
				return {
					height: height + buffer + 'px',
					top: '0px',
					bottom: 'unset',
				}
			} else {
				height = sidebarBottomX - tabcontentTop;
				return {
					height: height + buffer + 'px',
					top: 'unset',
					bottom: '0px',
				}
			}
		}
		return {}
	}

	const getUrl = (swatch_url: string) => {
		return swatch_url.indexOf('http') !== -1 ? swatch_url : `${process.env.REACT_APP_ASSET_URL}${swatch_url}`
	}

	return (
		<div style={{ background: 'var(--primary-bg-color)' }} className={clsx('desktop-sidebar xs:tw-hidden sm:tw-flex tw-flex-col tw-h-full sm:tw-w-[260px] lg:tw-w-[300px] xl:tw-w-[320px]', isShanecoPOS ? 'tw-overflow-y-auto tw-overflow-x-hidden tw-py-2' : 'tw-overflow-hidden', {
			'left': styles.sidebarOrder === 0,
			'right': styles.sidebarOrder === 1,
			'shaneco-desktop-sidebar': ['shaneco', 'shaneco-pos'].includes(subpath),
			'revolution-desktop-sidebar': ['revolution'].includes(subpath),
			'plum-diamonds-desktop-sidebar': ['plum-diamonds'].includes(subpath),
			'royal-hummingbird-desktop-sidebar': ['royal-hummingbird'].includes(subpath),
		})}>
			<div ref={isShanecoPOS ? undefined : sidebarRef} className={isShanecoPOS ? '' : 'tw-w-full tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-overflow-x-hidden tw-py-2'}>
				<div className={clsx("tw-flex tw-flex-col tw-items-center logo_container", subpath)}>
					<div className={clsx("tw-text-center tw-flex tw-justify-center tw-mt-2 desktop_logo_container", { 'tw-hidden': styles.showTopBar }, { 'tw-w-3/4': !['kay-jewelers', 'diamonds-direct'].includes(subpath), 'tw-px-4': ['kay-jewelers', 'diamonds-direct'].includes(subpath) })}>
						<button
							onClick={handleLogoClick}
							className="tw-text-inherit tw-bg-transparent tw-border-0 tw-cursor-pointer tw-flex"
						>
							<img className={clsx('brand-logo', {
								'royal-hummingbird-brand-logo': ['royal-hummingbird'].includes(subpath)
							})}
								alt="brand_logo" />
						</button>
					</div>
					<If condition={['legacy-touch-dev', 'legacy-touch'].includes(subpath)}>
						<div className={clsx("tw-text-center primary-font-family tw-mt-2 legacy-sub-title tw-italic")}>
							Hand-crafted and personalized
						</div>
						<div className={clsx("tw-text-center tw-text-lg primary-font-family tw-text-primary-color tw-w-5/6 tw-mt-2 tw-flex")} style={{ justifyContent: "center" }}>
							{/* <button
							onClick={() => setActiveModal('reviews')}
							className="tw-text-inherit tw-bg-transparent tw-border-0 tw-cursor-pointer tw-flex"
							style={{justifyContent: "center"}}
						> */}
							<div className="tw-flex legacy-stars-container">
								<svg className="legacy-star" width="17" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M10.9344 0.40332L13.293 7.66847H20.9325L14.7523 12.1574L17.1109 19.4226L10.9306 14.9336L4.75037 19.4226L7.10895 12.1574L0.928711 7.66847H8.56823L10.9344 0.40332Z" fill="#2574BB" />
								</svg>
								<svg className="legacy-star" width="17" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M10.9344 0.40332L13.293 7.66847H20.9325L14.7523 12.1574L17.1109 19.4226L10.9306 14.9336L4.75037 19.4226L7.10895 12.1574L0.928711 7.66847H8.56823L10.9344 0.40332Z" fill="#2574BB" />
								</svg>
								<svg className="legacy-star" width="17" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M10.9344 0.40332L13.293 7.66847H20.9325L14.7523 12.1574L17.1109 19.4226L10.9306 14.9336L4.75037 19.4226L7.10895 12.1574L0.928711 7.66847H8.56823L10.9344 0.40332Z" fill="#2574BB" />
								</svg>
								<svg className="legacy-star" width="17" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M10.9344 0.40332L13.293 7.66847H20.9325L14.7523 12.1574L17.1109 19.4226L10.9306 14.9336L4.75037 19.4226L7.10895 12.1574L0.928711 7.66847H8.56823L10.9344 0.40332Z" fill="#2574BB" />
								</svg>
								<svg className="legacy-star" width="17" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M10.9344 0.40332L13.293 7.66847H20.9325L14.7523 12.1574L17.1109 19.4226L10.9306 14.9336L4.75037 19.4226L7.10895 12.1574L0.928711 7.66847H8.56823L10.9344 0.40332Z" fill="#2574BB" />
								</svg>
							</div>
							<div className="legacy-review-count">
								316 Reviews
							</div>
							{/* </button> */}
						</div>
					</If>
					<div className={clsx("tw-text-right tw-text-lg primary-font-family tw-text-primary-color tw-w-5/6 tw-mt-2", { 'tw-hidden': !styles.logoSubText })}>
						RING BUILDER
					</div>
				</div>
				<If condition={['legacy-touch-dev', 'legacy-touch'].includes(subpath)}>
					<LegacyPinWithHelpModal
						className="desktop-sidebar-object"
						inputId="desktop-PIN"
						submitId="-PIN-submit"
						onValidated={(resp) => setIsPinValid(Boolean(resp?.isValid))}
					/>
				</If>
				<div className="tw-flex-1 tw-flex tw-flex-col tw-justify-between tw-w-full secondary-font-family desktop_sidebar_body">
					<If condition={styles.enablePoweredBy}>
						<p className="powered-by tw-mt-5 tw-text-center">
							<span className="tw-text-base">Powered by</span>
							<img className="tw-inline-block tw-h-6 tw-ml-2" src={lashbrookLogo} alt="Lashbrook Builder" />
						</p>
					</If>
					<If condition={subpath.includes('kay-jewelers')}>
						<p className="tw-mt-5 tw-text-center">
							<span className="tw-text-lg">Customize Your Ring</span>
						</p>
					</If>
					<div className={clsx("tw-flex-1 tw-flex tw-flex-col tw-w-full",
						{ 'tw-my-4': ['legacy-touch', 'legacy-touch-dev'].includes(subpath) },
						{ 'tw-my-6': !['legacy-touch', 'legacy-touch-dev'].includes(subpath) },
					)}>
						{pages.map(item => {
							const selectedInfo = getSelectedInfo(item);
							const selectedText = selectedInfo?.text || '';
							if (item.eventKey !== "pin") {
								return (
									<div
										id={item.eventKey}
										key={item.eventKey}
										className={clsx(`tw-flex tw-flex-col tw-justify-center tw-cursor-pointer tw-pl-3 tw-py-2 tw-border-gray-500 sidebar-tab`,
											{
												'shaneco-border': ['shaneco', 'shaneco-pos'].includes(subpath),
												'tw-bg-white tw-pr-4 active': selectedTab === item.eventKey,
												'kays-selected-sidebar': subpath.includes('kay-jewelers') && selectedTab === item.eventKey,
												'tw-border-t tw-mr-4': selectedTab !== item.eventKey,
												'mavilo': ['mavilo', 'mavilo-stage'].includes(subpath),
												'tw-border-y-2 tw-border-l-2 tw-border-r-0 tw-border-primary-border-color !tw-rounded-none': selectedTab === item.eventKey && styles.showfullBorder,
											},
										)}
										onClick={handleActiveTab?.(item.eventKey, false)}>
										<div className={clsx('tw-flex tw-items-center tw-justify-between', !isShanecoPOS && 'tw-gap-3')}>
											<If condition={!!item.title}>
												<div className={clsx("tw-flex tw-items-start tw-justify-center tw-flex-col tw-tab-text", { 'lt-title-container': ['legacy-touch', 'legacy-touch-dev'].includes(subpath) })}>
													<p
														className={clsx('tw-relative primary-font-family tw-text-base tw-font-medium tw-text-primary-color title', !isShanecoPOS && 'tw-whitespace-nowrap')}>
														<If condition={(item.eventKey !== 'material' || !['shaneco', 'shaneco-pos'].includes(subpath)) && (item.eventKey !== 'summary' || !['mavilo', 'mavilo-stage'].includes(subpath))}>
															{typeof item.title === 'string' && item.title}
															{typeof item.title === 'function' && item.title()}
														</If>
														<If condition={item.eventKey === 'material' && ['shaneco', 'shaneco-pos'].includes(subpath)}>
															Metal Type
														</If>
														<If condition={item.eventKey === 'summary' && ['mavilo', 'mavilo-stage'].includes(subpath)}>
															Retail Pricing
														</If>
													</p>
													{item?.disclaimer && <p className="tw-text-xs tw-mt-2">{item?.disclaimer}</p>}
												</div>
											</If>
											<If condition={item.eventKey !== 'summary'}>
												{isShanecoPOS ? (
													<div className={clsx("tw-flex tw-justify-center tw-items-center tw-flex-col tw-w-24", { 'tw-relative': selectedTab === item.eventKey })}>
														{selectedInfo?.url && !(/^None$/i).test(selectedText) &&
															<img className={clsx("tw-h-7 tw-w-7 tw-mb-1", { "tw-rounded-full": styles.isRoundedBorder, "rounded-square": !styles.isRoundedBorder })} alt="img" src={`${getUrl(selectedInfo?.url)}`} />}
														{selectedText && (
															<p className="tw-text-center tw-text-xs tw-text-primary-color">
																{selectedText !== 'Soccer' &&
																	selectedInfo?.url?.includes('CER')
																	? `Cerakote - ${selectedText}`
																	: selectedText}
															</p>
														)}
													</div>
												) : (
													<div className={clsx("tw-flex tw-flex-col tw-items-end", { 'tw-relative': selectedTab === item.eventKey })}>
														<div className="tw-flex tw-items-center tw-gap-1.5">
															{selectedText && (
																<p className="tw-text-xs tw-text-right tw-text-primary-color">
																	{selectedText !== 'Soccer' &&
																		selectedInfo?.url?.includes('CER')
																		? `Cerakote - ${selectedText}`
																		: selectedText}
																</p>
															)}
															{selectedInfo?.url && !(/^None$/i).test(selectedText) &&
																<img className={clsx("tw-h-7 tw-w-7 tw-shrink-0", { "tw-rounded-full": styles.isRoundedBorder, "rounded-square": !styles.isRoundedBorder })} alt="img" src={`${getUrl(selectedInfo?.url)}`} />}
														</div>
														{item.eventKey === 'engraving' && item.selectedItem === 'text' && !!selectedInfo?.engravingText && (
															<p className={clsx(FONT_MAP[selectedText], 'tw-leading-normal tw-text-right tw-text-primary-color tw-break-words engraving-font-size engraving-font-size-sidebar')}>
																{selectedInfo.engravingText}
															</p>
														)}
													</div>
												)}
											</If>
											<If condition={item.eventKey === 'summary'}>
												<If condition={['mavilo', 'mavilo-stage'].includes(subpath)}>
													<p className={clsx('tw-text-center tw-text-primary-color tw-text-lg mavilo-price')}>
														{price}
													</p>
												</If>
												<If condition={!['mavilo', 'mavilo-stage', 'shaneco', 'shaneco-pos'].includes(subpath)}>
													<div className={clsx('tw-text-xs tw-font-normal tw-flex tw-flex-col tw-justify-center', isShanecoPOS ? 'tw-items-center tw-pr-5' : 'tw-items-end')}>
														<span className="tw-leading-normal">{product?.magic_number}</span>
													</div>
												</If>
											</If>
										</div>
										<If condition={item.eventKey === 'summary' && ['mavilo', 'mavilo-stage'].includes(subpath)}>
											<div className='tw-mt-3'>
												<div>Mavilo Discounts Apply</div>
											</div>
											<div className='tw-mt-3'>
												<div>Style Number: {product?.magic_number}</div>
											</div>
										</If>
										<If condition={item.eventKey === 'summary' && subpath === 'rogers-jewelry'}>
											<div className='tw-text-center tw-mt-3 tw-px-8'>
												<div className="rogers-everything">Click here for summary and magic # needed to start your order</div>
											</div>
										</If>
										{/* <If condition={item.eventKey === 'summary' && ['revolution', 'stonebrook-jewelry', 'foundry'].includes(subpath)}>
										<div className='tw-text-right tw-mb-2 currency-message'>
											<div>*Pricing is shown in USD</div>
										</div>
									</If> */}
										{/* <If condition={item.eventKey === 'summary' && styles.showShippingEstimation && !['legacy-touch', 'legacy-touch-dev', 'kay-jewelers'].includes(subpath)}>
										<div className='tw-text-center tw-mt-4'>
											<If condition={subpath != 'happy-jewelers'}>
												<div>Ships as soon as:</div>
											</If>
											<If condition={['happy-jewelers'].includes(subpath)}>
												<div>Ships directly to Happy Jewelers as soon as:</div>
											</If>
											<If condition={!['henne'].includes(subpath)}>
												<div className='tw-font-bold'>{getLastestShippingDate(shippingOptions)}</div>
											</If>
											<If condition={['henne'].includes(subpath)}>
												<div className='tw-font-bold'>{getLastestShippingDateHenne(shippingOptions)}</div>
											</If>
										</div>
									</If> */}
										<If condition={!['legacy-touch-dev', 'legacy-touch'].includes(subpath)}>
											<If condition={selectedTab === item.eventKey}>
												{!styles.showfullBorder && <div id="active-h-border" style={horizontalStyle}></div>}
												{(isShanecoPOS || !isScrollable) && <div id="active-v-border" style={verticalStyle}></div>}
											</If>
										</If>
									</div>
								);
							}
							return null;
						})}
						{favouritesCount > 0 && pages.length > 0 && subpath !== 'shaneco-pos' && (
							<div
								id="favourites"
								className={clsx('tw-flex tw-flex-col tw-justify-center tw-cursor-pointer tw-pl-3 tw-py-2 tw-border-gray-500 sidebar-tab', {
									'shaneco-border': ['shaneco', 'shaneco-pos'].includes(subpath),
									'tw-bg-white tw-pr-4 active': showFavourites,
									'tw-border-t tw-mr-4': !showFavourites,
								})}
								onClick={onFavouritesClick}>
								<div className='tw-flex tw-items-center tw-justify-between'>
									<div className="tw-flex tw-items-center tw-gap-2 tw-tab-text">
										<i className="fa fa-heart tw-text-primary-color" aria-hidden />
										<p className="tw-relative primary-font-family tw-text-base tw-font-medium tw-text-primary-color title">
											Favorites
										</p>
									</div>
								</div>
								<If condition={showFavourites}>
									{!styles.showfullBorder && <div id="active-h-border" style={horizontalStyle}></div>}
									{(isShanecoPOS || !isScrollable) && <div id="active-v-border" style={verticalStyle}></div>}
								</If>
							</div>
						)}
						<div className='tw-border-t tw-border-gray-500 tw-mr-4'></div>
						<If condition={price && styles.showEstimatedTotal}>
							<div
								className={clsx(
									'tw-flex tw-justify-between tw-items-center tw-py-2 tw-border-gray-500 tw-border-solid tw-border-t tw-ml-4 half-border-b tw-pl-1 tw-pr-3 tw-h-[50px]',
									{
										'tw-border-t-0': selectedTab === 'summary'
									}
								)}
							>
								{/* <p className="tw-font-medium sidebar-title primary-font-family">Estimated Total</p>
							<p className="tw-text-lg tw-text-center tw-text-brand-color">{price}</p> */}
							</div>
						</If>
						<If condition={styles.showShippingEstimation && !['legacy-touch', 'legacy-touch-dev', 'kay-jewelers'].includes(subpath)}>
							{shippingOptions && (
								<div
									className='tw-text-center tw-text-[0.9rem] tw-cursor-pointer tw-pl-3 tw-py-2 tw-mr-4'
									onClick={(e) => {
										(window as any).__highlightShipDate = true;
										handleActiveTab?.('summary', false)?.(e as any);
										setTimeout(() => { window.dispatchEvent(new Event('highlight-ship-date')); }, 100);
									}}>
									<div className='tw-mt-4'>Ships as soon as</div>
									{shippingOptions?.same_day && shippingOptions?.next_day ? (
										<div className='tw-font-medium'>{formatSummaryDate(shippingOptions?.same_day)}</div>
									) : (
										<>
											{shippingOptions?.same_day && (
												<div className='tw-font-medium'>Same Day - {formatSummaryDate(shippingOptions?.same_day)}</div>
											)}
											{shippingOptions?.next_day && (
												<div className='tw-font-medium'>Next Day - {formatSummaryDate(shippingOptions?.next_day)}</div>
											)}
										</>
									)}
									{shippingOptions?.rush && (
										<div className='tw-font-medium'>Rush - {formatSummaryDate(shippingOptions?.rush)}</div>
									)}
									{shippingOptions?.standard && (
										<div className='tw-font-medium'>Standard - {formatSummaryDate(shippingOptions?.standard)}</div>
									)}
								</div>
							)}
						</If>
						<If condition={['kay-jewelers'].includes(subpath) && product?.shipping_options?.standard}>
							<div className='tw-text-center tw-text-[0.9rem] tw-cursor-pointer tw-pl-3 tw-py-2 tw-mr-4'
								onClick={(e) => {
									(window as any).__highlightShipDate = true;
									handleActiveTab?.('summary', false)?.(e as any);
									setTimeout(() => { window.dispatchEvent(new Event('highlight-ship-date')); }, 100);
								}}>
								<div>Ships by:</div>
								<div className='tw-font-bold'>{formatSummaryDate(product?.shipping_options?.standard)}</div>
							</div>
						</If>
					</div>
					<If condition={styles.showShippingEstimation && ['legacy-touch', 'legacy-touch-dev'].includes(subpath)}>
						{/* <div className='tw-text-center tw-mb-4 tw-mr-3' style={{border: "solid 1px #D0D5DD", borderRadius: "12px", padding: "12px", display: "flex"}}>
						<div style={{alignContent: "center"}}>
							<img src="https://lashbrook-wp-assets.s3.us-west-2.amazonaws.com/legacy_touch/lt_ship_icon.png" alt="" style={{padding: "10px", background: "#FFF", borderRadius: "12px", border: "solid 1px #D0D5DD"}}/>
						</div>
						<div style={{marginLeft: "12px", color: "rgb(82, 82, 91)"}}>
							<div style={{fontSize: '14px', fontWeight: '600'}}>Ships as soon as:</div>
							<div style={{fontSize: '14px', fontWeight: '400'}}>{getLastestShippingDate(shippingOptions)}</div>
						</div>
					</div> */}
						<div className='tw-text-center tw-mb-4 tw-mr-3' style={{ padding: "12px", display: "flex" }}>
							<div style={{ alignContent: "center" }}>
								<img src="https://lashbrook-wp-assets.s3.us-west-2.amazonaws.com/legacy_touch/lt_ship_icon.png" alt="" style={{ padding: "10px", background: "#FFF", borderRadius: "12px", border: "solid 1px #D0D5DD" }} />
							</div>
							<div style={{ marginLeft: "12px", color: "rgb(82, 82, 91)", alignContent: "center" }}>
								<div style={{ fontSize: '14px', fontWeight: '600' }}>Usually ships within 2 weeks</div>
							</div>
						</div>
					</If>
					<If condition={subpath === 'benari-jewelers'}>
						<div className='tw-mt-2' style={{ fontSize: '11px' }}>
							<p className="tw-mb-2">This ring includes a free at-home sizing kit that will arrive in 1-3 business days. You will be able to finalize your ring size after receiving the kit.</p>
							<p className="tw-mb-2">Free Standard Delivery: estimated delivery 4-6 weeks</p>
							<p className="tw-mb-2">Don't see your size available? You can special order a size. Contact us.</p>
							<p className="tw-mb-4 tw-pb-4">Please note, all custom jewelry sales are final.</p>
						</div>
					</If>
					{/* <If condition={['daniels-jewelers'].includes(subpath)}>
					<div className='tw-text-center tw-mb-4'>
						<div style={{color: '#573285'}}>Magic #: {product?.magic_number}</div>
					</div>
				</If>
				<If condition={['kay-jewelers'].includes(subpath)}>
					<div className='tw-text-center tw-mb-4'>
						<div>Magic #: {product?.magic_number}</div>
					</div>
				</If> */}
				</div>
			</div>
			<div className={clsx(isShanecoPOS ? 'tw-flex tw-flex-col tw-items-center tw-justify-center tw-mt-auto tw-pb-3' : 'tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-3', !isShanecoPOS && isScrollable && 'tw-shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]')} style={isShanecoPOS ? {} : { background: 'var(--primary-bg-color)', borderTop: isScrollable ? '2px solid var(--primary-border-color)' : 'none', marginLeft: '-12px', marginRight: '-12px', paddingLeft: '12px', paddingRight: '12px' }}>
				<If condition={saleDiscount > 0 && !!price}>
					<div className='tw-text-center'>
						<p className='tw-text-center tw-w-full'>
							<span className="tw-text-lg tw-text-center">
								{(() => { const n = parseFloat(String(price).replace(/[^\d.-]/g, '')); return Number.isFinite(n) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n * (1 - saleDiscount / 100)) : ''; })()}
							</span>
						</p>
						<p className="tw-text-center tw-w-full">
							<span className="tw-text-lg tw-line-through tw-text-gray-500">{price}</span>
						</p>
					</div>
					<div className='tw-text-center tw-mb-2 currency-message'>
						<div>*Pricing is shown in USD</div>
					</div>
				</If>
				<If condition={!(saleDiscount > 0) && !!price && ['revolution', 'stonebrook-jewelry', 'foundry'].includes(subpath)}>
					<div className='tw-text-center'>
						<p className="tw-text-center tw-w-full">
							<span className="tw-text-lg tw-text-center">{price}</span>
						</p>
					</div>
					<div className='tw-text-center tw-mb-2 currency-message'>
						<div>*Pricing is shown in USD</div>
					</div>
				</If>
				<If condition={!!product?.msrp}>
					<div className='tw-text-center tw-text-[1rem] tw-mb-4'>
						<p>
							<span className='tw-font-medium'>MSRP: </span>
							<span className='tw-line-through tw-text-gray-500'>{product?.msrp}</span>
						</p>
						<p>
							<span className='tw-font-medium'>Our Price: </span>
							<span>{price || <span className="tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-gray-200 tw-border-t-brand-color tw-rounded-full tw-animate-spin tw-align-middle" />}</span>
						</p>
					</div>
				</If>
				<If condition={['kay-jewelers', 'diamonds-direct'].includes(subpath)}>
					<div className='tw-text-center tw-text-[0.9rem] tw-py-3'>
						<p className={clsx('tw-text-center tw-text-primary-color tw-text-lg')}>
							{price || <span className="tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-gray-200 tw-border-t-brand-color tw-rounded-full tw-animate-spin tw-align-middle" />}
						</p>
					</div>
				</If>
				<div className="tw-flex tw-flex-col tw-gap-y-4 tw-w-full tw-pr-3">
					<If condition={['customize'].includes(subpath)}>
						<button className="tw-flex tw-items-center tw-cursor-pointer tw-bg-transparent" onClick={handleDone}>
							<img src={leftArrow} alt="right_arrow" />
							<span className='tw-pl-1.5 tw-underline tw-text-sm tw-text-primary-color'>Done Customizing</span>
						</button>
						<div className="tw-flex tw-items-center tw-w-full">
							<button className='tw-btn-brand !tw-text-white tw-w-full tw-px-9 tw-py-2 tw-rounded-full tw-font-semibold sm:tw-text-sm lg:tw-text-base' onClick={handleAddToCart}>Add to Cart - {product?.price || <span className="tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-white/30 tw-border-t-white tw-rounded-full tw-animate-spin tw-align-middle" />}</button>
						</div>
					</If>
					<If condition={styles.enableDefaultDone}>
						<button className='tw-btn-brand !tw-text-[color:var(--brand-text-color,#fff)] tw-w-3/4 tw-px-9 tw-py-2 tw-rounded-tertiary-border-radius tw-font-medium tw-mx-auto' onClick={handleDone}>
							{['johnAtencio', 'johnAtencio-stage'].includes(subpath) ? 'DONE' : 'Done'}{product?.msrp || price === '$0.00' || !price ? '' : ` - ${price}`}
						</button>
					</If>
					<If condition={['rogers-jewelry', 'daniels-jewelers', 'daniels-jewelers-dev', 'awardco'].includes(subpath)}>
						<button className='tw-btn-brand !tw-text-white tw-w-3/4 tw-px-9 tw-py-2 tw-rounded-tertiary-border-radius tw-font-medium tw-mx-auto' onClick={handleActiveTab?.("summary", false)}>
							Done - {price || <span className="tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-white/30 tw-border-t-white tw-rounded-full tw-animate-spin tw-align-middle" />}
						</button>
					</If>
					<If condition={['aslan-jewelers'].includes(subpath)}>
						<button className='tw-btn-brand tw-w-3/4 tw-px-9 tw-py-2 tw-rounded-tertiary-border-radius tw-font-medium tw-mx-auto' onClick={handleDone}>
							Done - {price || <span className="tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-white/30 tw-border-t-white tw-rounded-full tw-animate-spin tw-align-middle" />}
						</button>
					</If>
					<If condition={['mavilo', 'mavilo-stage'].includes(subpath)}>
						<button className='tw-btn-brand !tw-text-white tw-w-3/4 tw-px-9 tw-py-2 tw-rounded-tertiary-border-radius tw-font-medium tw-mx-auto' onClick={handleDone}>
							Connect
						</button>
					</If>
					<If condition={['revolution', 'stonebrook-jewelry', 'foundry'].includes(subpath)}>
						<button className='tw-btn-brand !tw-text-white tw-w-3/4 tw-px-9 tw-py-2 tw-rounded-tertiary-border-radius tw-font-medium tw-mx-auto' onClick={handleDone}>
							Finish Your Design
						</button>
					</If>
					<If condition={['legacy-touch', 'legacy-touch-dev'].includes(subpath)}>
						<button className='tw-btn-brand !tw-text-white tw-w-3/4 tw-px-9 tw-py-2 tw-rounded-tertiary-border-radius tw-font-medium tw-mx-auto' style={{ width: "100%" }} onClick={handleLegacyAddToCart}
						>
							Add to Cart - {price || <span className="tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-white/30 tw-border-t-white tw-rounded-full tw-animate-spin tw-align-middle" />}
						</button>
					</If>
					<If condition={['shaneco', 'shaneco-pos'].includes(subpath)}>
						<div className="pricing-box mb-4">
							<div className="pricing-norefund-acceptance tw-text-center tw-mb-4">
								<div className="disclaimer-text tw-mb-2">
									All custom ring purchases are final and cannot be returned.
								</div>
								<label className="disclaimer-input">
									<input
										className="tw-mr-3"
										type="checkbox"
										value="I understand"
										onClick={e => setAcknowledged(e.currentTarget.checked)}
									/>
									I understand
								</label>
							</div>
							<div className="action my-4 text-lg-center">
								<If condition={acknowledged}>
									<button className='!tw-bg-brand-color !tw-text-white tw-w-full tw-px-9 tw-py-2 tw-font-medium tw-mx-auto shaneco-button' style={subpath === 'shaneco-pos' ? { fontSize: '.95rem' } : undefined} onClick={handleDone}>
										ADD TO BAG{subpath === 'shaneco-pos' && price ? ` - ${price}` : ''}
									</button>
								</If>
								<If condition={!acknowledged}>
									<button className='!tw-bg-brand-color !tw-text-white tw-w-full tw-px-9 tw-py-2 tw-font-medium tw-mx-auto shaneco-button disabled' style={subpath === 'shaneco-pos' ? { fontSize: '.95rem' } : undefined} onClick={handleDone} disabled>
										ADD TO BAG{subpath === 'shaneco-pos' && price ? ` - ${price}` : ''}
									</button>
								</If>
							</div>
						</div>
					</If>
					<If condition={['tqDiamonds', 'tqDiamonds-stage'].includes(subpath)}>
						<button className='!tw-bg-brand-color !tw-text-white tw-w-3/4 tw-px-9 tw-py-2 tw-rounded-tertiary-border-radius tw-font-medium tw-mx-auto' onClick={handleAddToCart}>
							Add to Cart - {price || <span className="tw-inline-block tw-w-4 tw-h-4 tw-border-2 tw-border-white/30 tw-border-t-white tw-rounded-full tw-animate-spin tw-align-middle" />}
						</button>
					</If>
				</div>
			</div>
			{activeModal === 'reviews' && (
				<div
					className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-[1000]"
					onClick={(e) => { if (e.currentTarget === e.target) setActiveModal(null); }}
					role="dialog"
					aria-modal="true"
					aria-labelledby="need-pin-title"
				>
					<div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-w-[min(92vw,520px)] tw-p-6 tw-space-y-4">
						<div className="tw-flex tw-items-start tw-justify-between">
							<h2 id="need-pin-title" className="tw-text-lg tw-font-semibold tw-text-gray-900">Product Reviews</h2>
						</div>
						<div className="tw-text-sm tw-text-gray-700 tw-space-y-3">
							<p>Insert Reviews</p>
						</div>
						<div className="tw-flex tw-justify-end tw-gap-2">
							<button
								type="button"
								onClick={() => setActiveModal(null)}
								className="tw-bg-gray-800 tw-text-white tw-rounded-lg tw-px-4 tw-py-2"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export const handleLogoClick = () => {
	if (!window.origin.includes('ringdesignersstudio.com') && !window.origin.includes('localhost')) {
		window.location.href = '/';
	}
}

export default DesktopSidebar;
